import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import {
  PasswordResetConfirmRequest,
  type PasswordResetConfirmRequest as ResetReq,
} from '@staiter/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '~/components/primitives/Button';
import { Input } from '~/components/primitives/Input';
import { confirmPasswordReset } from '~/lib/api/auth';
import { ApiRequestError } from '~/lib/api/client';
import styles from './reset-password.module.css';

const SearchSchema = z.object({
  token: z.string().min(1).optional(),
});

export const Route = createFileRoute('/reset-password')({
  validateSearch: SearchSchema,
  component: ResetPasswordPage,
});

type Fatal = 'no_token' | 'invalid_or_expired' | 'network';

function ResetPasswordPage(): JSX.Element {
  const { token } = Route.useSearch();
  const [fatal, setFatal] = useState<Fatal | null>(token ? null : 'no_token');
  const [success, setSuccess] = useState<boolean>(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetReq>({
    resolver: zodResolver(PasswordResetConfirmRequest),
    defaultValues: { token: token ?? '', new_password: '', confirm_password: '' },
  });

  const onSubmit = async (values: ResetReq): Promise<void> => {
    setServerError(null);
    try {
      await confirmPasswordReset({ ...values, token: token ?? '' });
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.status === 429 && err.retry_after) {
          setRateLimitSeconds(err.retry_after);
          startCountdown(err.retry_after, setRateLimitSeconds);
          return;
        }
        if (err.status === 400) {
          setFatal('invalid_or_expired');
          return;
        }
        setServerError(err.message || m.reset_password_error_generic());
        return;
      }
      setServerError(m.login_network_error());
    }
  };

  if (fatal) {
    return (
      <main className={styles.page}>
        <section className={styles.card}>
          <div className={styles.brand} aria-hidden="true">
            <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
            STAITER
          </div>
          <div className={styles.fatal}>
            <h1 className={styles.fatalTitle}>
              {fatal === 'no_token' && m.reset_password_no_token_title()}
              {fatal === 'invalid_or_expired' && m.reset_password_expired_title()}
              {fatal === 'network' && m.reset_password_network_title()}
            </h1>
            <p className={styles.fatalBody}>
              {fatal === 'no_token' && m.reset_password_no_token_body()}
              {fatal === 'invalid_or_expired' && m.reset_password_expired_body()}
              {fatal === 'network' && m.reset_password_network_body()}
            </p>
            <a href="/auth/password-reset" className={styles.link}>
              {m.reset_password_request_new()}
            </a>
          </div>
        </section>
      </main>
    );
  }

  if (success) {
    return (
      <main className={styles.page}>
        <section className={styles.card}>
          <div className={styles.brand} aria-hidden="true">
            <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
            STAITER
          </div>
          <h1 className={styles.title}>{m.reset_password_success_title()}</h1>
          <p className={styles.lede}>{m.reset_password_success_body()}</p>
          <a href="/login" className={styles.link}>
            {m.accept_invite_back_to_login()}
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <section className={styles.card} aria-labelledby="reset-password-title">
        <div className={styles.brand} aria-hidden="true">
          <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
          STAITER
        </div>
        <h1 id="reset-password-title" className={styles.title}>
          {m.reset_password_title()}
        </h1>
        <p className={styles.lede}>{m.reset_password_lede()}</p>

        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Input
            label={m.reset_password_new()}
            type="password"
            autoComplete="new-password"
            autoFocus
            hint={m.reset_password_hint()}
            error={errors.new_password?.message}
            {...register('new_password')}
          />
          <Input
            label={m.reset_password_confirm()}
            type="password"
            autoComplete="new-password"
            error={errors.confirm_password?.message}
            {...register('confirm_password')}
          />

          {rateLimitSeconds !== null && (
            <p className={styles.rateLimit} role="status" aria-live="polite">
              {m.login_rate_limit({ seconds: rateLimitSeconds })}
            </p>
          )}
          {serverError && (
            <p className={styles.serverError} role="alert">
              {serverError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || rateLimitSeconds !== null}
            loading={isSubmitting}
          >
            {m.reset_password_submit()}
          </Button>
        </form>
      </section>
    </main>
  );
}

function startCountdown(initial: number, setter: (v: number | null) => void): void {
  let remaining = initial;
  const interval = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(interval);
      setter(null);
    } else {
      setter(remaining);
    }
  }, 1000);
}
